import type { App, AppSummary } from "@alch/dx-entities";
import type { UseQueryOptions } from "@tanstack/react-query";
import { trpc } from "@util/trpc/trpcClient";

export const GetAppsListInput = {
  includeProducts: false,
  includeWhitelistEntries: false,
};

export interface AppAllowlist {
  whitelistedAddresses: NonNullable<App["whitelistedAddressEntries"]>;
  whitelistedIps: NonNullable<App["whitelistedIpEntries"]>;
  whitelistedOrigins: NonNullable<App["whitelistedOriginEntries"]>;
}

interface UseAppsOptions<T>
  extends Pick<UseQueryOptions<App[], Error, T>, "select"> {
  enabled?: boolean;
}

interface UseAppSummariesOptions<T>
  extends Pick<UseQueryOptions<AppSummary[], Error, T>, "select"> {
  enabled?: boolean;
}

const selectAppProducts = (app: App): NonNullable<App["products"]> =>
  app.products ?? [];

const selectAppAllowlist = (app: App): AppAllowlist => ({
  whitelistedAddresses: app.whitelistedAddressEntries ?? [],
  whitelistedIps: app.whitelistedIpEntries ?? [],
  whitelistedOrigins: app.whitelistedOriginEntries ?? [],
});

const omitAppRelations = ({
  products: _products,
  whitelistedAddressEntries: _whitelistedAddressEntries,
  whitelistedOriginEntries: _whitelistedOriginEntries,
  whitelistedIpEntries: _whitelistedIpEntries,
  ...app
}: App): App => app;

const selectAppSummary = ({
  id,
  sid,
  name,
  description,
  authToken,
  timeCreated,
}: App): AppSummary => ({
  id,
  sid,
  name,
  description,
  authToken,
  timeCreated,
});

export function useApps<T = App[]>(opts?: UseAppsOptions<T>) {
  return trpc.apps.getApps.useQuery(GetAppsListInput, {
    enabled: opts?.enabled,
    select: opts?.select,
  });
}

export function useAppSummaries<T = AppSummary[]>(
  opts?: UseAppSummariesOptions<T>,
) {
  return trpc.apps.getAppSummaries.useQuery(undefined, {
    enabled: opts?.enabled,
    select: opts?.select,
  });
}

export function useAppById(id: number) {
  return trpc.apps.getApps.useQuery(GetAppsListInput, {
    select: (data) => data.find((app) => app.id === id),
  });
}

export function useSuspenseApps<T = App[]>(
  opts?: Pick<UseQueryOptions<App[], Error, T>, "select">,
) {
  return trpc.apps.getApps.useSuspenseQuery(GetAppsListInput, {
    select: opts?.select,
  });
}

export function useSuspenseAppSummaries<T = AppSummary[]>(
  opts?: Pick<UseQueryOptions<AppSummary[], Error, T>, "select">,
) {
  return trpc.apps.getAppSummaries.useSuspenseQuery(undefined, {
    select: opts?.select,
  });
}

export function useAppProducts(appId: string) {
  return trpc.apps.getApp.useQuery(
    { appId },
    {
      select: selectAppProducts,
    },
  );
}

export function useSuspenseAppProducts(appId: string) {
  return trpc.apps.getApp.useSuspenseQuery(
    { appId },
    {
      select: selectAppProducts,
    },
  );
}

export function useAppAllowlist(appId: string) {
  return trpc.apps.getApp.useQuery(
    { appId },
    {
      select: selectAppAllowlist,
    },
  );
}

export function useCreateApp() {
  const utils = trpc.useUtils();
  return trpc.apps.createApp.useMutation({
    onMutate: async () => {
      await Promise.all([
        utils.apps.getApps.cancel(GetAppsListInput),
        utils.apps.getAppSummaries.cancel(),
      ]);
    },
    onSuccess: (newApp) => {
      utils.apps.getApps.setData(GetAppsListInput, (current) => {
        const newAppListItem = omitAppRelations(newApp);
        if (!current) {
          return [newAppListItem];
        }
        return [...current, newAppListItem];
      });
      utils.apps.getAppSummaries.setData(undefined, (current) => {
        const newAppSummary = selectAppSummary(newApp);
        if (!current) {
          return [newAppSummary];
        }
        return [newAppSummary, ...current];
      });
    },
    onSettled: async () => {
      await Promise.all([
        utils.apps.getApps.invalidate(GetAppsListInput),
        utils.apps.getAppSummaries.invalidate(),
      ]);
    },
  });
}

export function useUpdateApp() {
  const utils = trpc.useUtils();

  return trpc.apps.updateApp.useMutation({
    onMutate: async (input) => {
      await Promise.all([
        utils.apps.getApps.cancel(GetAppsListInput),
        utils.apps.getAppSummaries.cancel(),
        utils.apps.getApp.cancel({ appId: input.appSid }),
      ]);
    },
    onSuccess: (updatedApp) => {
      utils.apps.getApps.setData(GetAppsListInput, (apps) => {
        const updatedAppListItem = omitAppRelations(updatedApp);
        return apps?.map((app) =>
          app.id !== updatedApp.id ? app : updatedAppListItem,
        );
      });
      utils.apps.getAppSummaries.setData(undefined, (apps) => {
        const updatedAppSummary = selectAppSummary(updatedApp);
        return apps?.map((app) =>
          app.id !== updatedApp.id ? app : updatedAppSummary,
        );
      });
      utils.apps.getApp.setData({ appId: updatedApp.sid }, updatedApp);
    },
    onSettled: async (_data, _error, input) => {
      await Promise.all([
        utils.apps.getApps.invalidate(GetAppsListInput),
        utils.apps.getAppSummaries.invalidate(),
        utils.apps.getApp.invalidate({ appId: input.appSid }),
      ]);
    },
  });
}

export function useRotateAppApiKey() {
  const utils = trpc.useUtils();

  return trpc.apps.rotateApiKey.useMutation({
    onMutate: async (input) => {
      await Promise.all([
        utils.apps.getApps.cancel(GetAppsListInput),
        utils.apps.getAppSummaries.cancel(),
        utils.apps.getApp.cancel({ appId: input.appSid }),
      ]);
    },
    onSuccess: (rotatedApp) => {
      utils.apps.getApps.setData(GetAppsListInput, (apps) => {
        const rotatedAppListItem = omitAppRelations(rotatedApp);
        return apps?.map((app) =>
          app.id !== rotatedApp.id ? app : rotatedAppListItem,
        );
      });
      utils.apps.getAppSummaries.setData(undefined, (apps) => {
        const rotatedAppSummary = selectAppSummary(rotatedApp);
        return apps?.map((app) =>
          app.id !== rotatedApp.id ? app : rotatedAppSummary,
        );
      });
      utils.apps.getApp.setData({ appId: rotatedApp.sid }, rotatedApp);
    },
    onSettled: async (_data, _error, input) => {
      await Promise.all([
        utils.apps.getApps.invalidate(GetAppsListInput),
        utils.apps.getAppSummaries.invalidate(),
        utils.apps.getApp.invalidate({ appId: input.appSid }),
      ]);
    },
  });
}

export function useDeleteApp() {
  const utils = trpc.useUtils();

  return trpc.apps.deleteApp.useMutation({
    onMutate: async (input) => {
      await Promise.all([
        utils.apps.getApps.cancel(GetAppsListInput),
        utils.apps.getAppSummaries.cancel(),
        utils.apps.getApp.cancel({ appId: input.appSid }),
      ]);
    },
    onSuccess: (_data, input) => {
      utils.apps.getApps.setData(GetAppsListInput, (apps) => {
        return apps?.filter((app) => app.sid !== input.appSid);
      });
      utils.apps.getAppSummaries.setData(undefined, (apps) => {
        return apps?.filter((app) => app.sid !== input.appSid);
      });
      utils.apps.getApp.setData({ appId: input.appSid }, undefined);
    },
    onSettled: async (_data, _error, input) => {
      await Promise.all([
        utils.apps.getApps.invalidate(GetAppsListInput),
        utils.apps.getAppSummaries.invalidate(),
        utils.apps.getApp.invalidate({ appId: input.appSid }),
      ]);
    },
  });
}
